import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Form, type FormSubmitHandler, useForm } from 'react-hook-form';
import axios from 'axios';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { config } from '@/config';
import { AuthShell } from './AuthShell';

interface ResetRequestForm {
  password: string;
  confirmPassword: string;
  token: string;
}

interface ResetRequest {
  password: string;
  token: string;
}

function describeResetError(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code;
    if (code === 'ERR_BAD_REQUEST') {
      return 'Invalid reset code. It may have already been used or expired — request a new one.';
    }
  }
  return 'There was a problem resetting your password. Please try again.';
}

export default function ResetPassword() {
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetFailure, setResetFailure] = useState('');
  const { hash } = useLocation();

  const {
    register,
    control,
    formState: { errors },
  } = useForm<ResetRequestForm>({ delayError: 750 });

  const onSubmit: FormSubmitHandler<ResetRequestForm> = ({ data }) => {
    const request: ResetRequest = { password: data.password, token: data.token };
    return axios
      .create()
      .post(`${config.backendApi}/api/auth/reset`, request)
      .then(
        () => { setResetSuccess(true); },
        (e: unknown) => { setResetFailure(describeResetError(e)); },
      );
  };

  if (resetSuccess) {
    return (
      <AuthShell title="Password reset">
        <div className="p-6 flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-sev-4/15 flex items-center justify-center text-sev-4 mb-4">
            <CheckCircle2 className="w-5 h-5" />
          </div>
          <p className="text-sm text-foreground mb-4">
            Your password has been reset. Sign in with your new password.
          </p>
          <Button asChild className="w-full">
            <Link to="/signin">Sign in</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password" subtitle="Choose a new password for your account.">
      <Form
        method="post"
        onSubmit={onSubmit}
        control={control}
        encType="application/json"
        className="p-6 space-y-4"
      >
        <div className="space-y-1.5">
          <Label htmlFor="reset-password">New password</Label>
          <Input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            {...register('password', { required: 'required' })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="reset-confirm">Confirm password</Label>
          <Input
            id="reset-confirm"
            type="password"
            autoComplete="new-password"
            aria-invalid={!!errors.confirmPassword}
            {...register('confirmPassword', {
              required: true,
              validate: (val: string, values) =>
                values.password === val || 'Passwords do not match',
            })}
          />
          {errors.confirmPassword ? (
            <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>
          ) : null}
        </div>

        <input type="hidden" value={hash.substring(1)} {...register('token')} />

        {resetFailure ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{resetFailure}</AlertDescription>
          </Alert>
        ) : null}

        <Button type="submit" className="w-full">
          Reset password
        </Button>
      </Form>
    </AuthShell>
  );
}
